import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import FoodCard from "@/components/base/FoodCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import { foodCategories, foods } from "@/mocks/foods";

const priceRanges = [
  { id: "all", label: "全部价格" },
  { id: "low", label: "30 元以下" },
  { id: "mid", label: "30 - 60 元" },
  { id: "high", label: "60 元以上" },
];

const sorts = [
  { id: "default", label: "综合推荐" },
  { id: "rating", label: "评分最高" },
  { id: "distance", label: "距离最近" },
  { id: "price", label: "价格从低到高" },
];

const PAGE_SIZE = 6;

export default function FoodCategory() {
  const [categoryId, setCategoryId] = useState("all");
  const [priceId, setPriceId] = useState("all");
  const [sortId, setSortId] = useState("default");
  const [page, setPage] = useState(1);

  const activeCategory = foodCategories.find((c) => c.id === categoryId);
  const categoryName = activeCategory ? activeCategory.name : "全部美食";

  usePageMeta(
    `${categoryName} - 美味的快递美食外卖`,
    `浏览美味的快递${categoryName}分类，涵盖中餐、西餐、甜点与饮品等多样美食，支持按价格与评分筛选排序，快速找到心仪餐品。`,
    `${categoryName},美味的快递,美食外卖,${categoryName}外卖,在线点餐,美食配送`
  );

  const filtered = useMemo(() => {
    let list = categoryId === "all" ? [...foods] : foods.filter((f) => f.categoryId === categoryId);

    if (priceId === "low") list = list.filter((f) => f.price < 30);
    else if (priceId === "mid") list = list.filter((f) => f.price >= 30 && f.price <= 60);
    else if (priceId === "high") list = list.filter((f) => f.price > 60);

    if (sortId === "rating") list.sort((a, b) => b.rating - a.rating);
    else if (sortId === "distance") list.sort((a, b) => a.distance - b.distance);
    else if (sortId === "price") list.sort((a, b) => a.price - b.price);

    return list;
  }, [categoryId, priceId, sortId]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageList = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [categoryId, priceId, sortId]);

  const switchCategory = (id: string) => setCategoryId(id);

  return (
    <div className="bg-background-50">
      <section className="bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "美食分类", to: "/food-category" }, { label: categoryName }]} />
          <h1 className="mt-4 font-heading text-2xl font-extrabold text-foreground-950 md:text-4xl">
            {categoryName} · 美食分类
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            {activeCategory ? activeCategory.desc : "汇集全城中餐、西餐、甜点与饮品，支持按价格区间、评分与距离筛选，帮你快速锁定心仪美味。"}
          </p>
        </div>
      </section>

      <section className="border-b border-background-200 bg-background-50">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-4 md:px-8">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => switchCategory("all")}
              className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition ${
                categoryId === "all"
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              <i className="ri-apps-2-line" />
              全部
            </button>
            {foodCategories.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => switchCategory(c.id)}
                className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition ${
                  categoryId === c.id
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                <i className={c.icon} />
                {c.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
        <div className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-100 p-4 md:flex-row md:items-center md:justify-between md:p-5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-bold text-foreground-800">价格</span>
            {priceRanges.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setPriceId(p.id)}
                className={`rounded-full px-3.5 py-1.5 text-sm transition ${
                  priceId === p.id
                    ? "bg-secondary-500 text-background-50"
                    : "bg-background-50 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-bold text-foreground-800">排序</span>
            {sorts.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setSortId(s.id)}
                className={`rounded-full px-3.5 py-1.5 text-sm transition ${
                  sortId === s.id
                    ? "bg-accent-500 text-background-50"
                    : "bg-background-50 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-5 flex items-center justify-between">
          <p className="text-sm text-foreground-500">
            共找到 <strong className="text-primary-600">{filtered.length}</strong> 道美食
            <span className="ml-2 rounded-full bg-background-100 px-2.5 py-0.5 text-xs text-foreground-500">演示数据</span>
          </p>
          <p className="hidden text-sm text-foreground-500 sm:block">
            第 {page} / {totalPages} 页
          </p>
        </div>

        {pageList.length > 0 ? (
          <div data-product-shop className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {pageList.map((food) => (
              <FoodCard key={food.id} food={food} />
            ))}
          </div>
        ) : (
          <div className="mt-5 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-search-line text-3xl" />
            </span>
            <h3 className="mt-4 font-heading text-lg font-bold text-foreground-900">没有符合条件的美食</h3>
            <p className="mt-2 text-sm text-foreground-500">试试调整价格区间或切换其他分类吧</p>
            <button
              type="button"
              onClick={() => {
                setCategoryId("all");
                setPriceId("all");
                setSortId("default");
              }}
              className="mt-5 rounded-full bg-primary-500 px-6 py-2.5 text-sm font-bold text-background-50 transition hover:bg-primary-600"
            >
              重置筛选条件
            </button>
          </div>
        )}

        {totalPages > 1 && (
          <div className="mt-10 flex items-center justify-center gap-2">
            <button
              type="button"
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-background-300 bg-background-50 text-foreground-700 transition hover:border-primary-300 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <i className="ri-arrow-left-s-line text-lg" />
            </button>
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setPage(i + 1)}
                className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold transition ${
                  page === i + 1
                    ? "bg-primary-500 text-background-50"
                    : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              type="button"
              disabled={page === totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-background-300 bg-background-50 text-foreground-700 transition hover:border-primary-300 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <i className="ri-arrow-right-s-line text-lg" />
            </button>
          </div>
        )}

        <div className="mt-12 rounded-lg border border-background-200 bg-primary-50 p-5 md:flex md:items-center md:justify-between md:p-6">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
              <i className="ri-e-bike-2-line text-2xl" />
            </span>
            <div>
              <p className="font-heading text-base font-bold text-foreground-950">还想寄件或查快递？</p>
              <p className="text-sm text-foreground-500">同城最快 30 分钟送达，全程可追踪</p>
            </div>
          </div>
          <div className="mt-4 flex flex-col gap-3 sm:flex-row md:mt-0">
            <Link
              to="/courier-send"
              className="flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-5 py-2.5 text-sm font-bold text-background-50 transition hover:bg-accent-600"
            >
              <i className="ri-send-plane-fill" />
              立即寄件
            </Link>
            <Link
              to="/courier-track"
              className="flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-bold text-foreground-800 transition hover:border-accent-300"
            >
              <i className="ri-search-eye-line" />
              查询快递
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}